import React from 'react';

interface MonthlySpendDataItem {
  month: string;
  amount: number;
  selected_account_amount?: number | null;
}

interface MonthlySpendChartProps {
  data: MonthlySpendDataItem[] | null;
  loading: boolean;
}

const MonthlySpendChart: React.FC<MonthlySpendChartProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Monthly Spend Trend
        </h3>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Monthly Spend Trend
        </h3>
        <div className="flex flex-col items-center justify-center h-64 text-center px-4">
          <svg className="h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400 font-medium mb-2">No monthly spend data available</p>
          <p className="text-sm text-gray-400 dark:text-gray-500">Configure your AWS environment to start tracking costs</p>
        </div>
      </div>
    );
  }

  const maxAmount = Math.max(...data.map((d) => d.amount));
  const hasAccountData = data.some(d => d.selected_account_amount != null);

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
        Monthly Spend Trend
      </h3>

      {/* Legend for stacked bars */}
      {hasAccountData && (
        <div className="flex items-center gap-4 mb-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span className="text-gray-600 dark:text-gray-400">Selected Account</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-200 dark:bg-blue-800 rounded"></div>
            <span className="text-gray-600 dark:text-gray-400">Other Accounts</span>
          </div>
        </div>
      )}

      {/* Bar chart */}
      <div className="relative h-64 flex items-end space-x-2">
        {data.map((item) => {
          const heightPercent = (item.amount / maxAmount) * 100;
          const [year, month] = item.month.split('-');
          const monthLabel = new Date(Number(year), Number(month) - 1).toLocaleDateString('en-US', {
            month: 'short',
          });

          // Calculate stacked bar heights
          const accountAmount = item.selected_account_amount || 0;
          const otherAmount = item.amount - accountAmount;
          const accountHeightPercent = (accountAmount / maxAmount) * 100;
          const otherHeightPercent = (otherAmount / maxAmount) * 100;

          return (
            <div key={item.month} className="flex-1 flex flex-col items-center">
              <div className="w-full flex flex-col items-center justify-end h-56">
                {hasAccountData ? (
                  // Stacked bar
                  <div className="w-full flex flex-col" style={{ height: `${heightPercent}%` }}>
                    {/* Other accounts portion (top) */}
                    {otherAmount > 0 && (
                      <div
                        className="w-full bg-blue-200 dark:bg-blue-800 hover:bg-blue-300 dark:hover:bg-blue-700 transition-colors cursor-pointer relative group"
                        style={{ height: `${(otherHeightPercent / heightPercent) * 100}%` }}
                      >
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          Total: ${item.amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </div>
                      </div>
                    )}
                    {/* Selected account portion (bottom) */}
                    {accountAmount > 0 && (
                      <div
                        className="w-full bg-blue-500 hover:bg-blue-600 transition-colors cursor-pointer relative group"
                        style={{
                          height: `${(accountHeightPercent / heightPercent) * 100}%`,
                          borderTopLeftRadius: otherAmount > 0 ? '0' : '0.25rem',
                          borderTopRightRadius: otherAmount > 0 ? '0' : '0.25rem'
                        }}
                      >
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          Account: ${accountAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Single bar
                  <div
                    className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer relative group"
                    style={{ height: `${heightPercent}%` }}
                  >
                    {/* Tooltip on hover */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      ${item.amount.toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                {monthLabel}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary stats */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-3 gap-4 text-sm">
        <div>
          <span className="text-gray-500 dark:text-gray-400">Average:</span>
          <span className="ml-2 font-semibold text-gray-900 dark:text-white">
            ${(data.reduce((sum, d) => sum + d.amount, 0) / data.length).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Lowest:</span>
          <span className="ml-2 font-semibold text-gray-900 dark:text-white">
            ${Math.min(...data.map((d) => d.amount)).toLocaleString()}
          </span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Highest:</span>
          <span className="ml-2 font-semibold text-gray-900 dark:text-white">
            ${maxAmount.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MonthlySpendChart;
